import React, { ReactElement, ReactNode } from "react";
import * as ui from "../native";
import { ContextData } from "./BuildContext";
import Notification from "../models/Notification";
import Administrator from "../models/Administrator";
import Invoice from "../models/Invoice";
import ThemeWrapper from "../components/ThemeWrapper";
import SearchRequest from "../models/SearchRequest";
import GlobalFunctions from "../utils/GlobalFunctions";
import NOKUser from "../models/NOKUser";
import { StyleThemeData } from "../components/ThemeWrapper";

export type RouterController = (node: ReactNode) => void;

const pageRouters: Map<string, RouterController> = new Map<
  string,
  RouterController
>();

export default class PageNavigator {
  public static defaultOverlay: ui.OverlayController;
  public static themeUpdateListener = () => {};
  public kIsWeb = true;
  public ctx: ContextData;
  public constructor(ctx: ContextData) {
    this.ctx = ctx;
  }
  public static addListener(target: string, listener: RouterController) {
    pageRouters.set(target, listener);
  }
  public static removeListener(target: string) {
    pageRouters.remove(target);
  }
  public static of(ctx: ContextData): PageNavigator {
    return new PageNavigator(ctx);
  }
  public wrapTheme(widget: ReactNode): ReactNode {
    let mediaQuery = ui.MediaQuery.of(this.ctx);

    return ThemeWrapper({ child: widget });
  }
  public push(
    replace: boolean,
    widget: ReactNode,
    title: string,
    path: string,
    target: string
  ): void {
    if (this.kIsWeb) {
      if (replace) {
        window.history.replaceState(null, title, path);
      } else {
        window.history.pushState(null, title, path);
      }
    }

    widget = this.wrapTheme(widget);

    if (!target) {
      target = "";
    }

    let listener = pageRouters.get(target);

    if (listener) {
      listener(widget);
    }
  }
  public pop(): void {
    this.close();
  }
  public close(): boolean {
    if (this.ctx.popup) {
      this.ctx.popup.hidePopup({});

      return true;
    } else {
      return false;
    }
  }
  public pushAdminChangePassWordPage(
    d3eParams?: Partial<{ replace: boolean; target: string; user: NOKUser }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let user = d3eParams?.user;

    this.push(
      replace,
      GlobalFunctions.AdminChangePassWordPage({ user: user }),
      "AdminChangePassWord",
      "#admin-change-pass-word-page",
      target
    );
  }
  public pushBillingDetailsView(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      obj: Invoice;
      admin: Administrator;
      user: NOKUser;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let obj = d3eParams?.obj;

    let admin = d3eParams?.admin;

    let user = d3eParams?.user;

    this.push(
      replace,
      GlobalFunctions.BillingDetailsView({
        obj: obj,
        admin: admin,
        user: user,
      }),
      "",
      "#billing-details-view",
      target
    );
  }
  public pushDashboardPage(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      administrator: Administrator;
      user: NOKUser;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let administrator = d3eParams?.administrator;

    let user = d3eParams?.user;

    this.push(
      replace,
      GlobalFunctions.DashboardPage({
        administrator: administrator,
        user: user,
      }),
      "",
      "#dashboard-page",
      target
    );
  }
  public pushForgotPasswordPage(
    d3eParams?: Partial<{ replace: boolean; target: string }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    this.push(
      replace,
      GlobalFunctions.ForgotPasswordPage({}),
      "",
      "#forgot password page",
      target
    );
  }
  public pushHomePage(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      admin: Administrator;
      user: NOKUser;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let admin = d3eParams?.admin;

    let user = d3eParams?.user;

    this.push(
      replace,
      GlobalFunctions.HomePage({ admin: admin, user: user }),
      "",
      "#home-page",
      target
    );
  }
  public pushHospitalTab(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      obj: Administrator;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let obj = d3eParams?.obj;

    this.push(
      replace,
      GlobalFunctions.HospitalTab({ obj: obj }),
      "",
      "#hospital-tab",
      target
    );
  }
  public pushLoginScreen(
    d3eParams?: Partial<{ replace: boolean; target: string }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    this.push(
      replace,
      GlobalFunctions.LoginScreen({}),
      "",
      "#login screen",
      target
    );
  }
  public pushSearchRequestResponseView(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      obj: SearchRequest;
      admin: Administrator;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let obj = d3eParams?.obj;

    let admin = d3eParams?.admin;

    this.push(
      replace,
      GlobalFunctions.SearchRequestResponseView({ obj: obj, admin: admin }),
      "",
      "#search-request-response-view",
      target
    );
  }
  public pushSettingTab(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      user: NOKUser;
      admin: Administrator;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let user = d3eParams?.user;

    let admin = d3eParams?.admin;

    this.push(
      replace,
      GlobalFunctions.SettingTab({ user: user, admin: admin }),
      "",
      "#setting-tab",
      target
    );
  }
  public pushStaffPage(
    d3eParams?: Partial<{ replace: boolean; target: string; user: NOKUser }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let user = d3eParams?.user;

    this.push(
      replace,
      GlobalFunctions.StaffPage({ user: user }),
      "",
      "#staff-page",
      target
    );
  }
  public pushStaffRequestTab(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      user: NOKUser;
      admin: Administrator;
      notification: Notification;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let user = d3eParams?.user;

    let admin = d3eParams?.admin;

    let notification = d3eParams?.notification;

    this.push(
      replace,
      GlobalFunctions.StaffRequestTab({
        user: user,
        admin: admin,
        notification: notification,
      }),
      "",
      "#staff-request-tab",
      target
    );
  }
  public pushSupportUserList(
    d3eParams?: Partial<{ replace: boolean; target: string }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    this.push(
      replace,
      GlobalFunctions.SupportUserList({}),
      "",
      "#support-user-list",
      target
    );
  }
  public pushSystemAdminInvoiceList(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      admin: Administrator;
      user: NOKUser;
      notification: Notification;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let admin = d3eParams?.admin;

    let user = d3eParams?.user;

    let notification = d3eParams?.notification;

    this.push(
      replace,
      GlobalFunctions.SystemAdminInvoiceList({
        admin: admin,
        user: user,
        notification: notification,
      }),
      "",
      "#system-admin-invoice-list",
      target
    );
  }
  public pushSystemAdminRequestList(
    d3eParams?: Partial<{
      replace: boolean;
      target: string;
      admin: Administrator;
      user: NOKUser;
      notification: Notification;
    }>
  ): void {
    let replace = d3eParams?.replace ?? true;

    let target = d3eParams?.target;

    let admin = d3eParams?.admin;

    let user = d3eParams?.user;

    let notification = d3eParams?.notification;

    this.push(
      replace,
      GlobalFunctions.SystemAdminRequestList({
        admin: admin,
        user: user,
        notification: notification,
      }),
      "",
      "#system-admin-request-list",
      target
    );
  }
  public applyTheme(d3eParams?: Partial<{ theme: string }>): void {
    let theme = d3eParams?.theme;

    /*
TODO
*/

    switch (theme) {
      case "D3ELight": {
        StyleThemeData.current = StyleThemeData.createD3ELight();

        PageNavigator.themeUpdateListener();

        /*
TODO ThemeBuilder.of(this.ctx).rebuild();
*/
        break;
      }
      default: {
        StyleThemeData.current = StyleThemeData.createD3ELight();

        PageNavigator.themeUpdateListener();

        /*
TODO ThemeBuilder.of(this.ctx).rebuild();
*/
      }
    }
  }
  public getProjectThemeNames(): string[] {
    return ["D3ELight"];
  }
  public getCurrentThemeName(): string {
    return StyleThemeData.current.themeName;
  }
}
